import api from "../services/api";

const PUSH_DISABLED_BY_USER_KEY = "estratizaPushDisabledByUser";
const PUSH_REPAIR_LAST_ATTEMPT_KEY = "estratizaPushRepairLastAttempt";
const PUSH_REPAIR_COOLDOWN_MS = 12 * 60 * 60 * 1000;
let pushRepairPromise = null;

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, "+")
    .replace(/_/g, "/");
  const rawData = window.atob(base64);
  return Uint8Array.from(rawData, char => char.charCodeAt(0));
}

function byteArraysEqual(left, right) {
  if (!left || !right) return false;
  const a = new Uint8Array(left);
  const b = new Uint8Array(right);
  if (a.length !== b.length) return false;
  return a.every((value, index) => value === b[index]);
}

export function getPushPlatform() {
  const ua = navigator.userAgent || "";
  const iPadDesktopMode =
    navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
  if (/iPad|iPhone|iPod/i.test(ua) || iPadDesktopMode) return "ios";
  if (/Android/i.test(ua)) return "android";
  if (/Windows/i.test(ua)) return "windows";
  if (/Macintosh|Mac OS X/i.test(ua)) return "macos";
  if (/Linux/i.test(ua)) return "linux";
  return "other";
}

export function getPushDeviceName() {
  const platform = getPushPlatform();
  const ua = navigator.userAgent || "";
  const browser = /Edg\//.test(ua)
    ? "Edge"
    : /CriOS|Chrome\//.test(ua)
      ? "Chrome"
      : /FxiOS|Firefox\//.test(ua)
        ? "Firefox"
        : /Safari\//.test(ua)
          ? "Safari"
          : "Navegador";
  const device = platform === "ios"
    ? (/iPad/i.test(ua) || navigator.platform === "MacIntel" ? "iPad" : "iPhone")
    : platform === "android"
      ? "Android"
      : platform === "windows"
        ? "Windows"
        : platform === "macos"
          ? "Mac"
          : "Dispositivo";
  return `${device} - ${browser}`;
}

export function isStandalonePwa() {
  return Boolean(
    window.matchMedia?.("(display-mode: standalone)")?.matches ||
    window.navigator.standalone === true
  );
}

export function getIosVersion() {
  if (getPushPlatform() !== "ios") return null;
  const ua = navigator.userAgent || "";
  const match = ua.match(/OS (\d+)[._](\d+)/i) || ua.match(/Version\/(\d+)\.(\d+)/i);
  if (!match) return null;
  return { major: Number(match[1]), minor: Number(match[2]) };
}

export function isSupportedIosVersion() {
  const version = getIosVersion();
  if (!version) return true;
  return version.major > 16 || (version.major === 16 && version.minor >= 4);
}

async function ensureServiceWorker() {
  if (!("serviceWorker" in navigator)) return null;
  const existing = await navigator.serviceWorker.getRegistration("/");
  if (existing) return navigator.serviceWorker.ready;
  await navigator.serviceWorker.register("/service-worker.js", { scope: "/" });
  return navigator.serviceWorker.ready;
}

const getStorageItem = key => {
  try {
    return window.localStorage.getItem(key);
  } catch (_err) {
    return null;
  }
};

const setStorageItem = (key, value) => {
  try {
    window.localStorage.setItem(key, value);
  } catch (_err) {
    // Storage indisponivel nao pode impedir a notificacao.
  }
};

const removeStorageItem = key => {
  try {
    window.localStorage.removeItem(key);
  } catch (_err) {
    // Storage indisponivel nao pode impedir a notificacao.
  }
};

async function currentSubscription() {
  const registration = await ensureServiceWorker();
  if (!registration || !("PushManager" in window)) return null;
  return registration.pushManager.getSubscription();
}

async function backendStatus(subscription) {
  if (!subscription) {
    return { backendConfirmed: false };
  }
  const { data } = await api.post("/push/status", {
    endpoint: subscription.endpoint
  });
  return data;
}

export async function getPushDiagnostics() {
  const https = window.isSecureContext && window.location.protocol === "https:";
  const serviceWorkerSupported = "serviceWorker" in navigator;
  const pushManagerSupported = "PushManager" in window;
  const notificationsSupported = "Notification" in window;
  const platform = getPushPlatform();
  const standalone = isStandalonePwa();
  const iosVersionSupported = isSupportedIosVersion();

  let registration = null;
  let subscription = null;
  let server = { backendConfirmed: false };
  let error = null;

  try {
    if (serviceWorkerSupported) registration = await ensureServiceWorker();
    if (registration && pushManagerSupported) {
      subscription = await registration.pushManager.getSubscription();
    }
    if (subscription) server = await backendStatus(subscription);
  } catch (err) {
    error = err?.response?.data?.error || err.message || "Falha no diagnóstico";
  }

  return {
    https,
    serviceWorkerSupported,
    serviceWorkerActive: Boolean(registration?.active),
    pushManagerSupported,
    notificationsSupported,
    permission: notificationsSupported ? Notification.permission : "unsupported",
    localSubscription: Boolean(subscription),
    backendConfirmed: Boolean(server.backendConfirmed),
    platform,
    deviceName: getPushDeviceName(),
    standalone,
    iosVersionSupported,
    error,
    ...server
  };
}

export async function activatePushDevice() {
  if (!window.isSecureContext || window.location.protocol !== "https:") {
    return { ok: false, code: "HTTPS_REQUIRED" };
  }
  if (
    !("Notification" in window) ||
    !("serviceWorker" in navigator) ||
    !("PushManager" in window)
  ) {
    return { ok: false, code: "NOT_SUPPORTED" };
  }
  if (getPushPlatform() === "ios" && !isSupportedIosVersion()) {
    return { ok: false, code: "IOS_VERSION_REQUIRED" };
  }
  if (getPushPlatform() === "ios" && !isStandalonePwa()) {
    return { ok: false, code: "IOS_INSTALL_REQUIRED" };
  }

  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    return {
      ok: false,
      code: permission === "denied" ? "PERMISSION_DENIED" : "PERMISSION_PENDING"
    };
  }

  try {
    const { data: vapid } = await api.get("/push/vapid-public-key");
    if (!vapid.publicKey) return { ok: false, code: "VAPID_UNAVAILABLE" };

    const registration = await ensureServiceWorker();
    if (!registration) return { ok: false, code: "SERVICE_WORKER_FAILED" };

    const expectedServerKey = urlBase64ToUint8Array(vapid.publicKey);
    let subscription = await registration.pushManager.getSubscription();
    const existingServerKey = subscription?.options?.applicationServerKey;

    if (
      subscription &&
      !byteArraysEqual(existingServerKey, expectedServerKey)
    ) {
      try {
        await api.post("/push/unsubscribe", {
          endpoint: subscription.endpoint
        });
      } catch (_err) {
        // O registro local ainda deve ser renovado se o backend ja o removeu.
      }
      await subscription.unsubscribe();
      subscription = null;
    }

    if (!subscription) {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: expectedServerKey
      });
    }

    const subscriptionJSON = subscription.toJSON();
    await api.post("/push/subscribe", {
      endpoint: subscriptionJSON.endpoint,
      keys: {
        p256dh: subscriptionJSON.keys.p256dh,
        auth: subscriptionJSON.keys.auth
      },
      platform: getPushPlatform(),
      deviceName: getPushDeviceName()
    });

    removeStorageItem(PUSH_DISABLED_BY_USER_KEY);
    removeStorageItem(PUSH_REPAIR_LAST_ATTEMPT_KEY);

    return { ok: true, code: "SUBSCRIBED", diagnostics: await getPushDiagnostics() };
  } catch (err) {
    return {
      ok: false,
      code: "SUBSCRIBE_FAILED",
      message: err?.response?.data?.error || err.message
    };
  }
}

export async function subscribeToPush() {
  const result = await activatePushDevice();
  return result.ok;
}

export async function unsubscribeFromPush() {
  try {
    const subscription = await currentSubscription();
    if (subscription) {
      await api.post("/push/unsubscribe", {
        endpoint: subscription.endpoint
      });
      await subscription.unsubscribe();
    }
    setStorageItem(PUSH_DISABLED_BY_USER_KEY, "1");
    return true;
  } catch (err) {
    console.error("[PUSH] Falha ao desativar este aparelho:", err);
    return false;
  }
}

async function repairPushDeviceInternal() {
  if (
    !window.isSecureContext ||
    window.location.protocol !== "https:" ||
    !("Notification" in window) ||
    !("serviceWorker" in navigator) ||
    !("PushManager" in window) ||
    Notification.permission !== "granted"
  ) {
    return { ok: false, code: "REPAIR_NOT_APPLICABLE" };
  }
  if (getPushPlatform() === "ios" && (!isSupportedIosVersion() || !isStandalonePwa())) {
    return { ok: false, code: "REPAIR_NOT_APPLICABLE" };
  }

  const registration = await ensureServiceWorker();
  if (!registration) return { ok: false, code: "SERVICE_WORKER_FAILED" };
  registration.update().catch(() => undefined);

  let subscription = await registration.pushManager.getSubscription();
  if (!subscription) {
    if (getStorageItem(PUSH_DISABLED_BY_USER_KEY) === "1") {
      return { ok: false, code: "DISABLED_BY_USER" };
    }
    return activatePushDevice();
  }

  const status = await backendStatus(subscription);
  if (!status.backendConfirmed || status.disabledAt) {
    return activatePushDevice();
  }
  if (!status.deliveryStalled) {
    return { ok: true, code: "HEALTHY", diagnostics: status };
  }

  const lastAttempt = Number(getStorageItem(PUSH_REPAIR_LAST_ATTEMPT_KEY) || 0);
  if (lastAttempt && Date.now() - lastAttempt < PUSH_REPAIR_COOLDOWN_MS) {
    return { ok: false, code: "REPAIR_COOLDOWN", diagnostics: status };
  }
  setStorageItem(PUSH_REPAIR_LAST_ATTEMPT_KEY, String(Date.now()));

  try {
    await api.post("/push/unsubscribe", { endpoint: subscription.endpoint });
  } catch (_err) {
    // A renovacao local continua segura mesmo se o registro remoto ja sumiu.
  }
  const removed = await subscription.unsubscribe();
  if (!removed) {
    return { ok: false, code: "LOCAL_UNSUBSCRIBE_FAILED", diagnostics: status };
  }
  subscription = null;
  return activatePushDevice();
}

export function repairPushDevice() {
  if (pushRepairPromise) return pushRepairPromise;
  pushRepairPromise = repairPushDeviceInternal()
    .catch(err => ({
      ok: false,
      code: "REPAIR_FAILED",
      message: err?.response?.data?.error || err.message
    }))
    .finally(() => {
      pushRepairPromise = null;
    });
  return pushRepairPromise;
}

export function startPushAutoRepair() {
  let timer = null;
  const schedule = delay => {
    if (timer) window.clearTimeout(timer);
    timer = window.setTimeout(() => {
      timer = null;
      repairPushDevice();
    }, delay);
  };
  const onVisibilityChange = () => {
    if (document.visibilityState === "visible") schedule(15000);
  };
  const onPageShow = () => schedule(15000);

  schedule(10000);
  document.addEventListener("visibilitychange", onVisibilityChange);
  window.addEventListener("pageshow", onPageShow);

  return () => {
    if (timer) window.clearTimeout(timer);
    document.removeEventListener("visibilitychange", onVisibilityChange);
    window.removeEventListener("pageshow", onPageShow);
  };
}

export async function isPushSubscribed() {
  const diagnostics = await getPushDiagnostics();
  return Boolean(
    diagnostics.localSubscription &&
    diagnostics.backendConfirmed &&
    !diagnostics.disabledAt
  );
}

export async function sendPushTestToThisDevice() {
  const subscription = await currentSubscription();
  if (!subscription) {
    return { ok: false, code: "NOT_SUBSCRIBED" };
  }
  try {
    const { data } = await api.post("/push/test", {
      endpoint: subscription.endpoint
    });
    return { ok: true, ...data };
  } catch (err) {
    return {
      ok: false,
      code: "TEST_FAILED",
      message: err?.response?.data?.error || err.message
    };
  }
}
